/**
 * Utility functions for GRN data transformation and processing
 */

import { AnyForNow, GrnData } from '../../features/grn/types';
import { FormatCurrencyLKR } from '../common/CurrencyFormatter';

// Transformed supplier data structure
interface TransformedSupplierData {
  supplierCode: string;
  supplierName: string;
  contactNo: string;
  email: string;
  address: string;
}

// Transformed PO data structure
interface TransformedPoData {
  poNumber: string;
  poDate: string | null;
  invoiceNumber: string;
  invoiceDate: string | null;
  dispatchNumber: string;
  dispatchDate: string | null;
  deliveryNoteNumber: string;
  deliveryDate: string | null;
  deliveryLocation: string;
  partialShipments: string;
  awbNumber: string;
  billOfLandingNumber: string;
  endate: string | null;
}

// Billing detail item structure
interface BillingDetailItem {
  label: string;
  value: number | string;
}

// Filter criteria structure
interface GrnFilters {
  grnId?: string;
  grnreturncode?: string;
  warehouse?: string;
  status?: string;
  fromDate?: string | Date;
  toDate?: string | Date;
}

/**
 * Transform supplier data for display
 * @param {Object} supplierData - Raw supplier data
 * @param {Object} grnHeader - GRN header data
 * @returns {Object} - Transformed supplier data
 */
export function transformSupplierData(supplierData: AnyForNow, grnHeader: AnyForNow): TransformedSupplierData {
  const data = grnHeader || supplierData || {};

  return {
    supplierCode: data.suprefcode || data.optSupplierRefCode || "-",
    supplierName: data.suppliername || data.optSupplierName || "-",
    contactNo: data.contactno || data.contactNo || "-",
    email: data.email || "-",
    address: [
      data.addresslinE1 || data.optSupplieraddressline1 || "-",
      data.addresslinE2 || data.optSupplieraddressline2 || "-",
      data.addresslinE3 || data.optSupplieraddressline3 || "-",
    ].filter(line => line !== "-").join("\n") || "-"
  };
}

/**
 * Transform PO data for display
 * @param {Object} poData - Raw PO data
 * @param {Object} grnHeader - GRN header data
 * @returns {Object} - Transformed PO data
 */
export function transformPoData(poData: AnyForNow, grnHeader: AnyForNow): TransformedPoData {
  const data = grnHeader || poData || {};

  return {
    poNumber: data.ponumber === "0" ? data.ponumber : (data.ponumber || data.poNumber || data.ponumber || "-"),
    poDate: data.podate || data.poDate || null,
    invoiceNumber: data.invoiceno || data.invoiceNumber || "-",
    invoiceDate: data.invoicedate || data.invoiceDate || null,
    dispatchNumber: data.dispatchno || data.dispatchNumber || "-",
    dispatchDate: data.dispatchedate || data.dispatchDate || null,
    deliveryNoteNumber: data.deliverynoteno || data.deliveryNoteNumber || "-",
    deliveryDate: data.deliverynotedate || data.deliveryDate || null,
    deliveryLocation: data.restname || data.warehouse || "-",
    partialShipments: data.ispartialship || data.partialShipments || "-",
    awbNumber: data.awbno || data.awbNumber || "-",
    billOfLandingNumber: data.blno || data.billOfLandingNumber || "-",
    endate: data.endate || null,
  };
}

/**
 * Transform billing data for display
 * @param {Object} summary - Billing summary data
 * @param {Object} processResponse - Process response data
 * @param {string} origin - Component origin
 * @returns {Array} - Array of billing details
 */
export function transformBillingData(summary: AnyForNow = {}, processResponse: AnyForNow = {}, origin: string = "", grnData?: Partial<GrnData>): BillingDetailItem[] {
  const processResponseData = (processResponse && Array.isArray(processResponse) && processResponse.length > 0)
    ? processResponse[0] : {};
  const calculatedSummary = processResponseData?.dtCalSummary?.[0] || {};

  const useProcessData = calculatedSummary && Object.keys(calculatedSummary).length > 0 &&
    (String(origin).toLowerCase() === "creategrn" || String(origin).toLowerCase() === "editgrn");

  // Calculate raw values
  const grossAmount = useProcessData
    ? calculatedSummary.totgross ?? 0
    : summary.totgross ?? 0;

  const discount = useProcessData
    ? calculatedSummary.totdiscount ?? 0
    : (summary.totproductdiscount && summary.totproductdiscount !== "0.00"
      ? summary.totproductdiscount
      : summary.totbulkdiscount ?? 0);

  const taxableValue = useProcessData
    ? (calculatedSummary.totgross ?? 0) - (calculatedSummary.totdiscount ?? 0)
    : summary.totnettgross ?? 0;

  // For billing summary tax display:
  // 1) Prefer dtCalSummary.tottax when available (source of truth from API)
  // 2) Then persisted billing vatAmount (EditGrn populates this from grnPaySummary.tottax)
  // 3) Finally, fall back to grnPaySummery.tottax (summary.tottax)
  const hasCalculatedTax = calculatedSummary && Object.keys(calculatedSummary).length > 0 && 
    (calculatedSummary.tottax !== undefined && calculatedSummary.tottax !== null);
  
  const tax = hasCalculatedTax
    ? Number(calculatedSummary.tottax ?? 0)
    : (Number(grnData?.billings?.vatAmount ?? 0) ||
       Number(summary?.tottax ?? 0) ||
       0);

  // Use calculated tax when available, otherwise use summary values
  const netValue = hasCalculatedTax
    ? (calculatedSummary.totgross ?? 0) - (calculatedSummary.totdiscount ?? 0) + tax
    : (useProcessData
      ? (calculatedSummary.totgross ?? 0) - (calculatedSummary.totdiscount ?? 0) + tax
      : parseFloat(summary.totnettgross || 0) + tax);

  const otherCharges = useProcessData
    ? calculatedSummary.totcharges ?? 0
    : summary.totcharges ?? 0;

  // Return formatted values with thousand separators
  return [
    {
      label: "Gross Amount",
      value: FormatCurrencyLKR(grossAmount),
    },
    {
      label: "Discount",
      value: FormatCurrencyLKR(discount),
    },
    {
      label: "Taxable Value",
      value: FormatCurrencyLKR(taxableValue),
    },
    {
      label: "Tax",
      value: FormatCurrencyLKR(tax),
    },
    {
      label: "Net Value",
      value: FormatCurrencyLKR(netValue),
    },
    {
      label: "Other Charges",
      value: FormatCurrencyLKR(otherCharges),
    }
  ];
}

/**
 * Calculate total invoice value
 * @param {Object} summary - Billing summary data
 * @param {Object} grnData - GRN data
 * @param {Object} processResponse - Process response data
 * @param {string} origin - Component origin
 * @returns {number} - Total invoice value
 */
export function calculateTotalInvoiceValue(summary: AnyForNow = {}, grnData: Partial<GrnData> = {}, processResponse: AnyForNow = {}, origin: string = ""): number {
  const processResponseData = (processResponse && Array.isArray(processResponse) && processResponse.length > 0)
    ? processResponse[0] : {};
  const calculatedSummary = processResponseData?.dtCalSummary?.[0] || {};

  const useProcessData = calculatedSummary && Object.keys(calculatedSummary).length > 0 &&
    (String(origin).toLowerCase() === "creategrn" || String(origin).toLowerCase() === "editgrn");

  if (useProcessData) {
    return calculatedSummary.totnett ?? 0;
  }

  const isEditGrn = String(origin || "").toLowerCase() === "editgrn";
  if (isEditGrn) {
    return Number(grnData.billings?.finalInvoiceValue || 0);
  }

  return Number(summary?.totinvoice || grnData.billings?.finalInvoiceValue || 0);
}

/**
 * Transform GRN list data with status information
 * @param {Array} data - Raw GRN data
 * @param {Array} statusTypes - Status types array
 * @param {function} dateFormatter - Date formatting function
 * @returns {Array} - Transformed GRN data with status
 */
export function transformGrnListWithStatus(data: AnyForNow[] = [], statusTypes: AnyForNow[] = [], dateFormatter: (date: any) => any = (date) => date): AnyForNow[] {
  if (!Array.isArray(data)) return [];

  return data.map((item, index) => {
    const matchedStatus = statusTypes.find(
      (status) => String(status.trsts || status.grnstatuscode) === String(item.trsts)
    );

    return {
      ...item,
      id: item.grncode || index,
      invoicedate: dateFormatter(item.invoicedate) || null,
      endate: dateFormatter(item.endate) || null,
      statusLabel: matchedStatus?.trenuserremark || matchedStatus?.grnstatus || "Unknown",
      statusColor: matchedStatus?.colorcode || "#0000",
      invoicedateRaw: item.invoicedate,
      endateRaw: item.endate,
      ponumber: item.ponumber,
      grnstatuscode: item.trsts,
    };
  });
}

/**
 * Filter GRN data based on criteria
 * @param {Array} data - GRN data array
 * @param {Object} filters - Filter criteria
 * @returns {Array} - Filtered data
 */
export function filterGrnData(data: AnyForNow[] = [], filters: GrnFilters = {}): AnyForNow[] {
  if (!Array.isArray(data)) return [];

  return data.filter((item) => {
    // GRN ID filter
    if (filters.grnId) {
      const search = filters.grnId.toLowerCase();
      if (!item.grncode?.toString().toLowerCase().includes(search)) {
        return false;
      }
    }

    // Return ID filter
    if (filters.grnreturncode) {
      const search = filters.grnreturncode.toLowerCase();
      if (!item.grnreturncode?.toString().toLowerCase().includes(search)) {
        return false;
      }
    }

    // Warehouse filter
    if (filters.warehouse) {
      if (String(item.restcode) !== filters.warehouse) {
        return false;
      }
    }

    // Status filter
    if (filters.status) {
      if (String(item.trsts) !== String(filters.status)) {
        return false;
      }
    }

    // Date range filter
    if (filters.fromDate && filters.toDate) {
      const from = new Date(filters.fromDate);
      const to = new Date(filters.toDate);
      const start = from < to ? from : to;
      const end = from < to ? to : from;

      const date = new Date(item.invoicedateRaw || item.endateRaw);
      if (date < start || date > end) {
        return false;
      }
    } else if (filters.fromDate) {
      const from = new Date(filters.fromDate);
      const date = new Date(item.invoicedateRaw || item.endateRaw);
      if (date < from) {
        return false;
      }
    } else if (filters.toDate) {
      const to = new Date(filters.toDate);
      const date = new Date(item.invoicedateRaw || item.endateRaw);
      if (date > to) {
        return false;
      }
    }

    return true;
  });
}
